import React, { type FC } from 'react';
import { formatFloat } from '../../../utility/utility_functions';
import { Loader } from '../../icons/OurIcons';
import { ValuationsType, UserValuationType, ValuationsTypeText } from '../../../utility/types';

type ValuationsTableProps = {
  propertyDetails: any;
  sqft: number;
  soldAvmResult: any;
  activeAvmResult: any;
  selectedValuation: number;
  userSelectedValuationType: UserValuationType;
  userEstimatedValue: number;
  userEstimatedValuePerSqft: number;
  soldCompsSix: any[];
  activeValuationType: ValuationsType;
  handleRowClick: (arvEst: number, arvSf: number, rowName: ValuationsType) => void;
}

const ValuationsTable: FC<ValuationsTableProps> = ({
  propertyDetails,
  sqft,
  soldAvmResult,
  activeAvmResult,
  selectedValuation,
  userSelectedValuationType,
  soldCompsSix,
  activeValuationType,
  userEstimatedValue,
  userEstimatedValuePerSqft,
  handleRowClick,
}) => {
  const getUserInputValAmount = (value: number): number | string => {
    if ((userSelectedValuationType === UserValuationType.ValuationAmount || userSelectedValuationType === null) && value) {
      return value;
    }

    if (userSelectedValuationType === UserValuationType.DollarPerSqFt && value) {
      let valAmount = value * sqft;
      return valAmount;
    }

    return null;
  };

  const getUserInputSqFtAmount = (value: number): number | string => {
    if ((userSelectedValuationType === UserValuationType.DollarPerSqFt || userSelectedValuationType === null) && value) {
      return value;
    }

    if (userSelectedValuationType === UserValuationType.ValuationAmount && value) {
      let dollarPerSqFt = value / sqft;
      return dollarPerSqFt;
    }

    return null;
  };

  // DollarPerSqft / sold AVM estimate
  const getPercentOfSelectedValuation = (value?: number): string | null => {
    if (value == null || selectedValuation === 0) {
      return null;
    }

    return ((value / selectedValuation) * 100).toFixed(2);
  };

  const getUserInputArvPercent = (value: number): number | string => {
    if ((userSelectedValuationType === UserValuationType.ValuationAmount || userSelectedValuationType === null) && value) {
      return getPercentOfSelectedValuation(value);
    }

    if (userSelectedValuationType === UserValuationType.DollarPerSqFt && value) {
      // let valAmount = (value * sqft);
      return getPercentOfSelectedValuation(value);
    }

    return null;
  };

  // 6 & 15 comps
  const avmFifteen = soldAvmResult?.comps?.slice(0, 15);

  const getAvmSixCompAvgSqFt = (): any => {
    let sqFtArr = soldCompsSix?.map((a) => a?.property.sqft).filter((a) => a != null);

    return (sqFtArr?.reduce((accumulator, currentVal) => accumulator + currentVal) / 6).toFixed(0);
  };

  const getAvgDollarPerSqFt = (avmCompsArr): any => {
    let dollarPerSqFtArr = avmCompsArr?.map((a) => a?.sold_dollar_per_sqft).filter((a) => a != null);

    if (dollarPerSqFtArr?.length === 15) {
      return (dollarPerSqFtArr?.reduce((accumulator, currentVal) => accumulator + currentVal) / 15);
    }

    if (dollarPerSqFtArr?.length === 6) {
      return (dollarPerSqFtArr?.reduce((accumulator, currentVal) => accumulator + currentVal) / 6);
    }

    return null;
  };

  const getValAmountFromSqFt = (avmCompsArr): any => {
    if (avmCompsArr?.length === 15) {
      return soldAvmResult?.result?.all_comp_dollar_per_sqft * soldAvmResult?.result?.avg_comp_sqft;
    }

    if (avmCompsArr?.length === 6) {
      return getAvgDollarPerSqFt(avmCompsArr) * getAvmSixCompAvgSqFt();
    }

    return null;
  };

  const getPercentOfArvFromAvgSqFt = (avmCompsArr): number | string => {
    const CompEstimatedValue = getValAmountFromSqFt(avmCompsArr);

    return getPercentOfSelectedValuation(CompEstimatedValue);
  };

  const onRowClick = (arvEst: number, arvSf: number, rowName: ValuationsType) => {
    if (rowName !== activeValuationType) {
      handleRowClick(arvEst, arvSf, rowName);
    }
  };

  if (!soldAvmResult) {
    return (
      <div className='w-screen content-center px-10'>
        <div className='mx-auto mt-10 w-96'>
          <Loader />
        </div>
      </div>
    );
  }

  const thHorizontalStyling = 'py-2 px-1';
  const thVerticalStyling = 'font-medium text-gray-900 py-2';
  const trStylingInactive = 'hover:cursor-pointer hover:bg-gray-100 text-gray-500 py-2';
  const trStylingActive = 'bg-indigo-600 bg-opacity-25 text-gray-500 py-2';

  return (
    <div id='valuations-table' className='mx-2 border rounded-lg '>
      <table id='valuations-table-table' className='min-w-full divide-y divide-gray-300 whitespace-nowrap text-center'>
        <thead id='valuations-table-header' className='divide-y divide-gray-300 rounded-t-lg'>
          <tr id='valuations-table-header-row' className='bg-indigo-600 text-white whitespace-nowrap divide-x divide-gray-300'>
            <th className='rounded-tl-lg w-[200px]'>
              {/* leave empty */}
            </th>
            <th className={thHorizontalStyling}>
            Valuation
            </th>
            <th className={thHorizontalStyling}>
            $/Sq FT
            </th>
            <th className={`${thHorizontalStyling} rounded-tr-lg`}>
            % of ARV
            </th>
          </tr>
        </thead>
        <tbody id='valuations-table-body' className='rounded-lg text-center whitespace-nowrap divide-y divide-gray-300'>
          <tr
            id='valuation-tax-value-row'
            data-autotag={`${activeValuationType === ValuationsType.TaxValue ? 'active-row' : 'inactive-row'}-tax-value-valuation-row`}
            className={`${activeValuationType === ValuationsType.TaxValue ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(propertyDetails?.assessed_value, propertyDetails?.assessed_value / sqft, ValuationsType.TaxValue)}
          >
            <th className={`${thVerticalStyling} rounded-bl-lg`}>
              {ValuationsTypeText.TaxValue}
            </th>
            <td>
              {propertyDetails?.assessed_value ? `$${formatFloat(propertyDetails?.assessed_value, 2)}` : '-'}
            </td>
            <td>
              {propertyDetails?.assessed_value ? `$${formatFloat(propertyDetails?.assessed_value / sqft, 2)}` : '-'}
            </td>
            <td className='rounded-br-lg'>
              {selectedValuation && propertyDetails?.assessed_value
                ? `${getPercentOfSelectedValuation(propertyDetails?.assessed_value)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-active-avm-row'
            className={`${activeValuationType === ValuationsType.ActiveAvm ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(activeAvmResult?.result?.estimated_value, activeAvmResult?.result?.estimated_dollar_per_sqft, ValuationsType.ActiveAvm)}
            data-autotag={`${activeValuationType === ValuationsType.ActiveAvm ? 'active-row' : 'inactive-row'}-active-avm-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.ActiveAvm}
            </th>
            <td>
              ${formatFloat(activeAvmResult?.result?.estimated_value, 0) ?? '-'}
            </td>
            <td>
              ${formatFloat(activeAvmResult?.result?.estimated_dollar_per_sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && activeAvmResult?.result?.estimated_value
                ? `${getPercentOfSelectedValuation(activeAvmResult?.result?.estimated_value)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-inactive-avm-row'
            className={`${activeValuationType === ValuationsType.InactiveAvm ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(soldAvmResult?.result?.estimated_value, soldAvmResult?.result?.estimated_dollar_per_sqft, ValuationsType.InactiveAvm)}
            data-autotag={`${activeValuationType === ValuationsType.InactiveAvm ? 'active-row' : 'inactive-row'}-inactive-avm-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.InactiveAvm}
            </th>
            <td>
              ${formatFloat(soldAvmResult?.result?.estimated_value, 0) ?? '-'}
            </td>
            <td>
              ${formatFloat(soldAvmResult?.result?.estimated_dollar_per_sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && activeAvmResult?.result?.estimated_value
                ? `${getPercentOfSelectedValuation(soldAvmResult?.result?.estimated_value)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-fifteen-comp-row'
            className={`${activeValuationType === ValuationsType.FifteenComp ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(soldAvmResult?.result?.all_comp_avg_sold_price, soldAvmResult?.result?.all_comp_avg_sold_price / sqft, ValuationsType.FifteenComp)}
            data-autotag={`${activeValuationType === ValuationsType.FifteenComp ? 'active-row' : 'inactive-row'}-fifteen-comp-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.FifteenComp}
            </th>
            <td>
              ${formatFloat(soldAvmResult?.result?.all_comp_avg_sold_price, 2) ?? '-'}
            </td>
            <td>
              ${formatFloat(soldAvmResult?.result?.all_comp_avg_sold_price / sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && soldAvmResult?.result?.all_comp_avg_sold_price
                ? `${getPercentOfSelectedValuation(soldAvmResult?.result?.all_comp_avg_sold_price)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-fifteen-comp-sqft-row'
            className={`${activeValuationType === ValuationsType.FifteenCompSqFt ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(getValAmountFromSqFt(avmFifteen), getValAmountFromSqFt(avmFifteen) / sqft, ValuationsType.FifteenCompSqFt)}
            data-autotag={`${activeValuationType === ValuationsType.FifteenCompSqFt ? 'active-row' : 'inactive-row'}-fifteen-comp-sqft-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.FifteenCompSqFt}
            </th>
            <td>
              ${formatFloat(getValAmountFromSqFt(avmFifteen), 0) ?? '-'}
            </td>
            <td>
              ${formatFloat(getValAmountFromSqFt(avmFifteen) / sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && avmFifteen
                ? `${formatFloat(getPercentOfArvFromAvgSqFt(avmFifteen), 2)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-six-comp-row'
            className={`${activeValuationType === ValuationsType.SixComp ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(soldAvmResult?.result?.closest_avg_sold_price, soldAvmResult?.result?.closest_avg_sold_price / sqft, ValuationsType.SixComp)}
            data-autotag={`${activeValuationType === ValuationsType.SixComp ? 'active-row' : 'inactive-row'}-six-comp-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.SixComp}
            </th>
            <td>
              ${formatFloat(soldAvmResult?.result?.closest_avg_sold_price, 0) ?? '-'}
            </td>
            <td>
              ${formatFloat(soldAvmResult?.result?.closest_avg_sold_price / sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && soldCompsSix
                ? `${getPercentOfSelectedValuation(soldAvmResult?.result?.closest_avg_sold_price)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-six-comp-sqft-row'
            className={`${activeValuationType === ValuationsType.SixCompSqFt ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => onRowClick(getValAmountFromSqFt(soldCompsSix), getValAmountFromSqFt(soldCompsSix) / sqft, ValuationsType.SixCompSqFt)}
            data-autotag={`${activeValuationType === ValuationsType.SixCompSqFt ? 'active-row' : 'inactive-row'}-six-comp-sqft-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.SixCompSqFt}
            </th>
            <td>
              ${formatFloat(getValAmountFromSqFt(soldCompsSix), 2) ?? '-'}
            </td>
            <td>
              ${formatFloat(getValAmountFromSqFt(soldCompsSix) / sqft, 2) ?? '-'}
            </td>
            <td>
              {selectedValuation && soldCompsSix
                ? `${formatFloat(getPercentOfArvFromAvgSqFt(soldCompsSix), 2)}%`
                : '-'
              }
            </td>
          </tr>
          <tr
            id='valuation-user-input-row'
            className={`${activeValuationType === ValuationsType.UserInput ? `${trStylingActive}` : `${trStylingInactive}`}`}
            onClick={() => {
              onRowClick(getUserInputValAmount(userEstimatedValue) as number, getUserInputSqFtAmount(userEstimatedValuePerSqft) as number, ValuationsType.UserInput);
            }}
            data-autotag={`${activeValuationType === ValuationsType.UserInput ? 'active-row' : 'inactive-row'}-user-input-valuation-row`}
          >
            <th className={thVerticalStyling}>
              {ValuationsTypeText.UserInput}
            </th>
            <td>
              {userEstimatedValue ? `$${formatFloat(userEstimatedValue)}` : (userEstimatedValue ? `$${formatFloat(userEstimatedValue)}` : '-')}
            </td>
            <td>
              {userEstimatedValuePerSqft ? `$${formatFloat(userEstimatedValuePerSqft)}` : (userEstimatedValuePerSqft ? `$${formatFloat(userEstimatedValuePerSqft)}` : '-')}
            </td>
            <td>
              {userEstimatedValue ? `${formatFloat(getUserInputArvPercent(userEstimatedValue), 0)}%` : (userEstimatedValue ? `${formatFloat(getUserInputArvPercent(userEstimatedValue), 0)}%` : '-')}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
export default ValuationsTable;
